/** User management: the member table, role help and the invitation dialog. */
const en = {
  title: 'User Management',
  countIn: (n: number, org: string) => `${n} users in ${org}`,
  addUser: 'Add user',
  needAdmin: 'You need an admin or owner role to manage users.',
  saved: 'Saved.',
  somethingWrong: 'Something went wrong',
  roleNames: {
    OWNER: 'Owner',
    ADMIN: 'Admin',
    MEMBER: 'Member',
    VIEWER: 'Viewer',
  } as Record<string, string>,
  roleHelp: {
    OWNER:
      'Owns the organization: billing, subscription, and everything an admin can do. The last owner cannot be demoted or disabled.',
    ADMIN: 'Manages this organization: projects, API keys, users, integrations, alerts.',
    MEMBER: 'Works tickets, watches replays, uses analytics.',
    VIEWER: 'Read-only access to replays and analytics.',
  } as Record<string, string>,
  rolesNote:
    'Owner owns the organization (billing and subscription on top of everything an admin can do; the last owner can’t be removed). Admin manages this organization: projects, keys, users, integrations. These are organization roles only: none of them grants platform administration, which is held by the operator running this deployment.',
  upgradeForTester: ' Upgrade under Billing to turn it on.',
  tester: {
    notIncluded: (plan: string) =>
      `Tester mode isn't included in the ${plan} plan, so flagged members can't use it.`,
    unlimited: (plan: string, used: number) => `${plan} plan · unlimited testers (${used} in use)`,
    overCap: (plan: string, cap: number, used: number) =>
      `${plan} plan includes ${cap} testers but ${used} are flagged. Revoke ${used - cap} or upgrade.`,
    inUse: (plan: string, used: number, cap: number) =>
      `${plan} plan · ${used} of ${cap} testers in use`,
    columnHint: (hint: string) =>
      `May use tester mode and open tickets from a session. ${hint}`,
  },
  columns: {
    user: 'User',
    role: 'Role',
    tester: 'Tester',
    remoteControl: 'Remote control',
    status: 'Status',
    source: 'Source',
    lastSignIn: 'Last sign-in',
    actions: 'Actions',
  },
  remoteControlHint:
    'May initiate remote control on a live session (visitor consent still required)',
  you: 'you',
  statuses: { disabled: 'Disabled', pending: 'Pending', active: 'Active' },
  pendingHint: 'Invited. Activates when they set their password from the email',
  sources: { sso: 'SSO', local: 'Local' },
  never: 'never',
  passwordHints: {
    sso: 'SSO account. Password is managed by the identity provider',
    resend: 'Resend invitation',
    reset: 'Email a password-reset link',
  },
  disableHints: {
    self: "You can't disable your own account",
    enable: 'Enable',
    disable: 'Disable',
  },
  link: {
    invitation: 'Invitation',
    reset: 'Password-reset link',
    sent: (what: string, email: string) => `${what} emailed to ${email}.`,
    failed: (what: string) =>
      `${what} could not be sent. Check the email settings under Platform Admin → Email.`,
  },
  dialog: {
    title: 'Add user',
    intro:
      'Adds a person to this organization. They receive an email with a link to choose their own password. The account stays Pending until they do, so no password is ever shared between you.',
    email: 'Email',
    fullName: 'Full name (optional)',
    role: 'Role',
    roleHelper:
      'Roles apply to this organization only. None of them grant platform administration.',
    sending: 'Sending…',
    send: 'Send invitation',
    invited: (email: string) =>
      `Invited ${email}. They'll set their own password from the email. The account activates once they do.`,
    createdNoMailWithReason: (email: string, reason: string) =>
      `Created ${email}, but the invitation email was not sent. ${reason} Their setup link is still valid: use “Resend invitation” on their row once mail works.`,
    createdNoMail: (email: string) =>
      `Created ${email}, but the invitation email could not be sent. Configure email under Platform Admin → Email, then use “Resend invitation” on their row.`,
  },
};

const tr: typeof en = {
  title: 'Kullanıcı Yönetimi',
  countIn: (n: number, org: string) => `${org} içinde ${n} kullanıcı`,
  addUser: 'Kullanıcı ekle',
  needAdmin: 'Kullanıcıları yönetmek için yönetici ya da sahip rolü gerekir.',
  saved: 'Kaydedildi.',
  somethingWrong: 'Bir şeyler ters gitti',
  roleNames: {
    OWNER: 'Sahip',
    ADMIN: 'Yönetici',
    MEMBER: 'Üye',
    VIEWER: 'İzleyici',
  },
  roleHelp: {
    OWNER:
      'Organizasyonun sahibi: faturalandırma, abonelik ve bir yöneticinin yapabildiği her şey. Son sahibin rolü düşürülemez ve hesabı kapatılamaz.',
    ADMIN: 'Bu organizasyonu yönetir: projeler, API anahtarları, kullanıcılar, entegrasyonlar, uyarılar.',
    MEMBER: 'Talepleri işler, kayıtları izler, analitiği kullanır.',
    VIEWER: 'Kayıtlara ve analitiğe salt okunur erişim.',
  },
  rolesNote:
    'Sahip organizasyonun sahibidir (yöneticinin yapabildiği her şeyin üstüne faturalandırma ve abonelik; son sahip silinemez). Yönetici bu organizasyonu yönetir: projeler, anahtarlar, kullanıcılar, entegrasyonlar. Bunlar yalnızca organizasyon rolleridir: hiçbiri platform yöneticiliği vermez; o, bu kurulumu işleten operatördedir.',
  upgradeForTester: ' Açmak için Faturalandırma’dan yükseltin.',
  tester: {
    notIncluded: (plan: string) =>
      `Test modu ${plan} planına dahil değil, bu yüzden işaretli üyeler kullanamaz.`,
    unlimited: (plan: string, used: number) =>
      `${plan} planı · sınırsız test kullanıcısı (${used} kullanımda)`,
    overCap: (plan: string, cap: number, used: number) =>
      `${plan} planı ${cap} test kullanıcısı içeriyor ama ${used} tanesi işaretli. ${used - cap} tanesini kaldırın ya da yükseltin.`,
    inUse: (plan: string, used: number, cap: number) =>
      `${plan} planı · ${cap} test kullanıcısından ${used} tanesi kullanımda`,
    columnHint: (hint: string) =>
      `Test modunu kullanabilir ve bir oturumdan talep açabilir. ${hint}`,
  },
  columns: {
    user: 'Kullanıcı',
    role: 'Rol',
    tester: 'Test',
    remoteControl: 'Uzaktan kontrol',
    status: 'Durum',
    source: 'Kaynak',
    lastSignIn: 'Son giriş',
    actions: 'İşlemler',
  },
  remoteControlHint:
    'Canlı bir oturumda uzaktan kontrol başlatabilir (ziyaretçi onayı yine de gerekir)',
  you: 'siz',
  statuses: { disabled: 'Kapalı', pending: 'Beklemede', active: 'Etkin' },
  pendingHint: 'Davet edildi. E-postadaki bağlantıdan parolasını belirleyince etkinleşir',
  sources: { sso: 'SSO', local: 'Yerel' },
  never: 'hiç',
  passwordHints: {
    sso: 'SSO hesabı. Parolayı kimlik sağlayıcı yönetir',
    resend: 'Daveti yeniden gönder',
    reset: 'Parola sıfırlama bağlantısı gönder',
  },
  disableHints: {
    self: 'Kendi hesabınızı kapatamazsınız',
    enable: 'Aç',
    disable: 'Kapat',
  },
  link: {
    invitation: 'Davet',
    reset: 'Parola sıfırlama bağlantısı',
    sent: (what: string, email: string) => `${what} ${email} adresine gönderildi.`,
    failed: (what: string) =>
      `${what} gönderilemedi. Platform Yönetimi → E-posta altındaki ayarları kontrol edin.`,
  },
  dialog: {
    title: 'Kullanıcı ekle',
    intro:
      'Bu organizasyona bir kişi ekler. Kendi parolasını seçmesi için bağlantı içeren bir e-posta alır. Parolasını belirleyene kadar hesap Beklemede kalır, böylece aranızda hiç parola paylaşılmaz.',
    email: 'E-posta',
    fullName: 'Ad soyad (isteğe bağlı)',
    role: 'Rol',
    roleHelper:
      'Roller yalnızca bu organizasyon için geçerlidir. Hiçbiri platform yöneticiliği vermez.',
    sending: 'Gönderiliyor…',
    send: 'Daveti gönder',
    invited: (email: string) =>
      `${email} davet edildi. Parolasını e-postadan kendisi belirleyecek. Bunu yapınca hesap etkinleşir.`,
    createdNoMailWithReason: (email: string, reason: string) =>
      `${email} oluşturuldu ama davet e-postası gönderilemedi. ${reason} Kurulum bağlantısı hâlâ geçerli: e-posta çalışmaya başlayınca satırındaki “Daveti yeniden gönder”i kullanın.`,
    createdNoMail: (email: string) =>
      `${email} oluşturuldu ama davet e-postası gönderilemedi. Platform Yönetimi → E-posta altından e-postayı yapılandırın, sonra satırındaki “Daveti yeniden gönder”i kullanın.`,
  },
};

export const members = { en, tr };
